import { User } from '@prisma/client'
import { Divider, Form, Radio, Select, Switch, Typography } from 'antd'
import React from 'react'
import { CommonClient } from '~/agents/common/client'
import { AppSessionType } from '~/features/appSession/common'
import { FeatureFormConfigurationClient } from '~/features/formConfiguration/client'
import { ProviderAccountAuthorization } from '~/features/formConfiguration/client/components/CustomInputs/ProviderAccountAuthorization'
import { SelectProviderAccount } from '~/features/formConfiguration/client/components/CustomInputs/SelectProviderAccount'
import { OnboardingValues } from '../../common'

type Props = {
  user: User
  appSession: AppSessionType
}

export const Settings: React.FC<Props> = ({ user, appSession }) => {
  const configuration = { timezone: user.timezone }

  const firstFollowUpOptions = Array.from({ length: 7 }, (_, i) => i + 2) // 2–8
  const secondFollowUpOptions = Array.from({ length: 7 }, (_, i) => i + 4) // 4–10

  const values = (appSession?.values?.onboarding ?? {}) as OnboardingValues

  const valuesSafe = {
    ...values,
    followUp: {
      ...values.followUp,
      first: values.followUp?.first ?? 3,
      second: values.followUp?.second ?? 5,
    },
  } as OnboardingValues

  const logic = CommonClient.Form.useLogic<OnboardingValues>({
    appSession,
    cleanValues(values) {
      const valuesClean = { ...values }

      if (values.emailServices?.provider === 'outlook') {
        valuesClean.authorizations = {}
      }

      if (!values.daily?.activated) {
        valuesClean.daily = { activated: false }
      }

      if (values.followUp.first !== valuesSafe.followUp.first) {
        let valueFollowUpSecond = values.followUp.second

        if (values.followUp.first > valueFollowUpSecond - 2) {
          valueFollowUpSecond = values.followUp.first + 2
        }

        valuesClean.followUp.second = valueFollowUpSecond
      }

      return valuesClean
    },
  })
  const hasPermissionCorrupted = (permissions: any[]) => {
    return permissions.some(permission => !!permission?.[0]?.isCorrupted)
  }

  const handleSubmit = async (valuesUpdated: OnboardingValues) => {
    const permissionGmailModify = valuesUpdated.authorizations?.modify
    const permissionGmailDraft = valuesUpdated.authorizations?.draft
    const permissionGmailSend = valuesUpdated.authorizations?.send

    const isCorrupted = appSession?.statusOnboarding === 'CORRUPTED'
    const isStillCorrupted = hasPermissionCorrupted([
      permissionGmailModify,
      permissionGmailDraft,
      permissionGmailSend,
    ])

    if (isCorrupted && !isStillCorrupted) {
      await logic.submitOnboarding()
    }
  }

  return (
    <>
      <CommonClient.Form.Wrapper
        {...logic}
        values={valuesSafe}
        onSubmit={handleSubmit}
      >
        <Typography.Title level={4} style={{ margin: 0 }}>
          Inbox
        </Typography.Title>

        <Divider className="my-3" />

        <CommonClient.Form.InputWrapper
          label="Inbox"
          description="Inbox provider connected"
        >
          <Form.Item
            name={['emailServices', 'provider']}
            rules={[{ required: true }]}
          >
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              options={[
                {
                  label: 'Google',
                  value: 'google',
                },
                {
                  label: 'Outlook',
                  value: 'outlook',
                },
                {
                  label: 'IMAP',
                  value: 'nylas',
                },
              ]}
            />
          </Form.Item>
        </CommonClient.Form.InputWrapper>

        <Form.Item<OnboardingValues> shouldUpdate={true}>
          {options => {
            const provider = options.getFieldValue([
              'emailServices',
              'provider',
            ])

            if (provider === 'nylas') {
              return (
                <Form.Item
                  name={['emailServices', 'accountNylas']}
                  label="IMAP Account"
                >
                  <SelectProviderAccount providerKey="nylas" />
                </Form.Item>
              )
            }

            if (provider === 'google') {
              return (
                <Form.Item
                  name={['emailServices', 'accountGmail']}
                  label="Gmail Account"
                >
                  <SelectProviderAccount providerKey="gmail" />
                </Form.Item>
              )
            }

            if (provider === 'outlook') {
              return (
                <Form.Item
                  name={['emailServices', 'accountOutlookMail']}
                  label="Outlook Account"
                >
                  <SelectProviderAccount providerKey="outlookMail" />
                </Form.Item>
              )
            }
          }}
        </Form.Item>

        <Form.Item<OnboardingValues> shouldUpdate={true}>
          {options => {
            const provider = options.getFieldValue([
              'emailServices',
              'provider',
            ])

            const providerAccountId = options.getFieldValue([
              'emailServices',
              'accountGmail',
            ])

            if (!providerAccountId || provider !== 'google') {
              return <></>
            }

            return (
              <>
                <Form.Item
                  name={['authorizations', 'modify']}
                  rules={[
                    {
                      validator: (_, value) =>
                        value?.[0]?.isCorrupted
                          ? Promise.reject()
                          : Promise.resolve(),
                      message: 'Authorization is not valid',
                    },
                  ]}
                >
                  <ProviderAccountAuthorization
                    providerAccountId={providerAccountId}
                    scope="https://www.googleapis.com/auth/gmail.modify"
                    ui={{
                      title: 'Manage Your Inbox',
                      iconAntd: 'GoogleOutlined',
                      description: 'Allow me to manage your inbox',
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name={['authorizations', 'send']}
                  rules={[
                    {
                      validator: (_, value) =>
                        value?.[0]?.isCorrupted
                          ? Promise.reject()
                          : Promise.resolve(),
                      message: 'Authorization is not valid',
                    },
                  ]}
                >
                  <ProviderAccountAuthorization
                    providerAccountId={providerAccountId}
                    scope="https://www.googleapis.com/auth/gmail.send"
                    ui={{
                      title: 'Send Emails',
                      iconAntd: 'GoogleOutlined',
                      description: 'Allow me to send emails',
                    }}
                  />
                </Form.Item>
              </>
            )
          }}
        </Form.Item>

        <Typography.Title level={4} style={{ margin: 0 }}>
          Daily
        </Typography.Title>

        <Divider className="my-3" />

        <CommonClient.Form.InputWrapper
          label="Daily Leads"
          description="Receive daily list of leads"
        >
          <Form.Item name={['daily', 'activated']}>
            <Switch size="small" />
          </Form.Item>
        </CommonClient.Form.InputWrapper>

        <Form.Item shouldUpdate={true}>
          {options => {
            const isActive = options.getFieldValue(['daily', 'activated'])

            if (!isActive) {
              return <></>
            }

            return (
              <>
                <CommonClient.Form.InputWrapper
                  label="Message time"
                  description="Choose at what time you want to receive the leads"
                >
                  <Form.Item
                    name={['daily', 'rule']}
                    getValueFromEvent={items => items[0]}
                  >
                    <FeatureFormConfigurationClient.RRulePicker
                      type="DAILY"
                      formValuesConfiguration={configuration}
                    />
                  </Form.Item>
                </CommonClient.Form.InputWrapper>

                <CommonClient.Form.InputWrapper
                  label="Contact"
                  description="Contact the leads sourced straight away"
                >
                  <Form.Item name={['daily', 'autopilot']}>
                    <Switch size="small" />
                  </Form.Item>
                </CommonClient.Form.InputWrapper>
              </>
            )
          }}
        </Form.Item>

        <CommonClient.Form.InputWrapper
          label="Daily Follow-up"
          description="Follow-up with leads that haven't responded"
        >
          <Form.Item name={['followUp', 'activated']}>
            <Switch size="small" />
          </Form.Item>
        </CommonClient.Form.InputWrapper>

        <Form.Item shouldUpdate={true}>
          {options => {
            const isActive = options.getFieldValue(['followUp', 'activated'])

            if (!isActive) {
              return <></>
            }

            return (
              <>
                <CommonClient.Form.InputWrapper
                  label="Follow-up time"
                  description="Choose at what time you want to follow-up"
                >
                  <Form.Item
                    name={['followUp', 'rule']}
                    getValueFromEvent={items => items[0]}
                  >
                    <FeatureFormConfigurationClient.RRulePicker
                      type="DAILY"
                      formValuesConfiguration={configuration}
                    />
                  </Form.Item>
                </CommonClient.Form.InputWrapper>

                <CommonClient.Form.InputWrapper
                  label="First follow-up"
                  description="Choose number of days for first follow-up"
                >
                  <Form.Item name={['followUp', 'first']}>
                    <Select variant="filled" style={{ width: '110px' }}>
                      {firstFollowUpOptions.map(day => (
                        <Select.Option key={day} value={day}>
                          {day}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </CommonClient.Form.InputWrapper>

                <CommonClient.Form.InputWrapper
                  label="Second follow-up"
                  description="Choose number of days for second follow-up"
                >
                  <Form.Item name={['followUp', 'second']}>
                    <Select variant="filled" style={{ width: '110px' }}>
                      {secondFollowUpOptions.map(day => {
                        const valueFollowUpFirst = options.getFieldValue([
                          'followUp',
                          'first',
                        ])

                        const disabled =
                          valueFollowUpFirst !== null &&
                          day - valueFollowUpFirst < 2

                        return (
                          <Select.Option
                            key={day}
                            value={day}
                            disabled={disabled}
                          >
                            {day}
                          </Select.Option>
                        )
                      })}
                    </Select>
                  </Form.Item>
                </CommonClient.Form.InputWrapper>
              </>
            )
          }}
        </Form.Item>
      </CommonClient.Form.Wrapper>
    </>
  )
}
